import { Upload, X } from 'lucide-react';
import { useId, useState } from 'react';
import { LOGO_ACCEPTED_TYPES, LOGO_MAX_BYTES, type PrintLogo } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { ImageReadError, readImageDataUrl } from '#client/lib/read-image-file';

interface LogoFieldProps {
  logo: PrintLogo;
  onChange: (patch: Partial<PrintLogo>) => void;
}

/**
 * The letterhead mark.
 *
 * Read into a data URL in the browser and stored inside the template document,
 * rather than uploaded to a file endpoint. That keeps a template a single
 * self-contained JSON object — exportable, copyable between environments,
 * revertible — with no second store to keep in step and no orphaned blob when a
 * template is deleted. The 512 KB ceiling is far more than a 30 mm mark needs at
 * print resolution, and it is checked here so the error arrives before the save
 * rather than after it.
 *
 * The reading half is shared with `SignatureField` via `readImageDataUrl`. The
 * two differ only in their ceiling and their accepted types, which is why those
 * are arguments — and why both now come from shared/schemas/signature.ts rather
 * than being restated as literals the server could drift away from.
 */
export function LogoField({ logo, onChange }: LogoFieldProps) {
  const inputId = useId();
  const [error, setError] = useState<string>();

  async function handleFile(file: File | undefined) {
    setError(undefined);
    if (!file) return;

    try {
      const dataUrl = await readImageDataUrl(file, {
        maxBytes: LOGO_MAX_BYTES,
        accepted: LOGO_ACCEPTED_TYPES,
      });
      onChange({ data_url: dataUrl });
    } catch (cause) {
      setError(cause instanceof ImageReadError ? cause.message : 'Could not read that file.');
    }
  }

  return (
    <div className="c-logo-field">
      <div className="c-logo-field__preview">
        {logo.data_url ? (
          <img src={logo.data_url} alt="Uploaded letterhead mark" />
        ) : (
          <span className="c-logo-field__default">Built-in NDC mark</span>
        )}
      </div>

      <div className="c-logo-field__actions">
        <input
          id={inputId}
          className="sr-only"
          type="file"
          accept={LOGO_ACCEPTED_TYPES.join(',')}
          onChange={(event) => void handleFile(event.target.files?.[0])}
        />
        <Button
          icon={<Upload />}
          // The native file input is visually hidden and driven by its label, so
          // the button carries the app's styling and the input keeps its
          // keyboard and screen-reader behaviour.
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {logo.data_url ? 'Replace' : 'Upload'}
        </Button>

        {logo.data_url && (
          <Button icon={<X />} variant="ghost" onClick={() => onChange({ data_url: null })}>
            Use the built-in mark
          </Button>
        )}
      </div>

      {error && (
        <p className="c-logo-field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
